<template>
  <modal-view title="Edit Release" @close-modal="$emit('close-modal')">
    <div class="space-y-5 p-4">
      <!-- Read-only info -->
      <div class="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
        <div class="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span class="text-gray-500 dark:text-gray-400">Version</span>
            <p class="font-mono font-medium text-gray-900 dark:text-white">
              {{ release.version }}
            </p>
          </div>
          <div>
            <span class="text-gray-500 dark:text-gray-400">Platform</span>
            <p class="font-medium text-gray-900 dark:text-white">
              {{ release.platform }}
            </p>
          </div>
          <div>
            <span class="text-gray-500 dark:text-gray-400">Target</span>
            <p class="font-medium text-gray-900 dark:text-white">
              {{ targetLabel }}
            </p>
          </div>
          <div v-if="release.artifact_original_name">
            <span class="text-gray-500 dark:text-gray-400">File</span>
            <p class="text-gray-900 dark:text-white truncate">
              {{ release.artifact_original_name }}
            </p>
          </div>
          <div v-if="release.checksum_sha256">
            <span class="text-gray-500 dark:text-gray-400"
              >Checksum (SHA-256)</span
            >
            <p
              class="font-mono text-xs text-gray-600 dark:text-gray-400 truncate"
            >
              {{ release.checksum_sha256 }}
            </p>
          </div>
        </div>
      </div>

      <!-- Editable fields -->
      <div>
        <label
          class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          Release Notes
        </label>
        <textarea
          v-model="form.release_notes"
          rows="4"
          class="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-white"
        ></textarea>
      </div>

      <div class="flex items-center gap-6">
        <label
          class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
        >
          <input
            type="checkbox"
            v-model="form.is_required"
            class="rounded border-gray-300"
          />
          Required update
        </label>
        <label
          class="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
        >
          <input
            type="checkbox"
            v-model="form.is_published"
            class="rounded border-gray-300"
          />
          Published
        </label>
      </div>

      <!-- Error -->
      <p v-if="errorMessage" class="text-sm text-red-600 dark:text-red-400">
        {{ errorMessage }}
      </p>

      <!-- Actions -->
      <div class="flex justify-end gap-3 pt-2">
        <button
          type="button"
          @click="$emit('close-modal')"
          class="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          Cancel
        </button>
        <button
          type="button"
          @click="handleSave"
          :disabled="saving"
          class="rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {{ saving ? "Saving..." : "Save Changes" }}
        </button>
      </div>
    </div>
  </modal-view>
</template>

<script>
import { mapActions } from "vuex";
import ModalView from "@/components/modals/ModalView.vue";
import { releaseTargetLabel } from "@/constants/releaseTargets";

export default {
  name: "EditReleaseModal",
  components: { ModalView },
  props: {
    release: { type: Object, required: true },
  },
  emits: ["close-modal", "release-updated"],
  computed: {
    targetLabel() {
      return releaseTargetLabel(this.release);
    },
  },
  data() {
    return {
      saving: false,
      errorMessage: "",
      form: {
        release_notes: this.release.release_notes || "",
        is_required: this.release.is_required || false,
        is_published: this.release.is_published || false,
      },
    };
  },
  methods: {
    ...mapActions("adminReleases", ["updateRelease"]),
    async handleSave() {
      this.saving = true;
      this.errorMessage = "";
      try {
        await this.updateRelease({
          releaseId: this.release.id,
          payload: {
            release_notes: this.form.release_notes,
            is_required: this.form.is_required,
            is_published: this.form.is_published,
          },
        });
        this.$emit("release-updated");
      } catch (error) {
        this.errorMessage =
          error?.response?.data?.message || "Failed to update release.";
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
